import React, { useCallback, useEffect, useMemo } from 'react';
import { Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import type { ProposalId } from '@shared/models/proposals.model';
import { useSelectedVersion } from '@client/store/ui';
import { useNavigateToElementListSelection } from '@client/hooks/use-router.hooks';
import { Page } from '@client/store/ui/ui.model';
import { SidePanel } from '@ui/layout';
import { resolveVersionForProposal } from '@shared/helpers/actions/resolve-version-for-proposal.helpers';
import { warnProposalCondenseConflicts } from '@shared/helpers/actions/proposal-condense-conflicts.helpers';
import { ProjectChangeType } from '@shared/helpers/pipeline/update-engine.models';
import { useApplyProjectChanges, useProject } from '@client/store/project';
import { createMarketEstimatesSnapshot } from '@shared/helpers/product-market-estimates.helpers';
import { useDisplayCurrency } from '@features/currency/selected-display-currency.hook';
import { useActionsForProposal } from '../hooks/use-actions-for-proposal.hooks';
import { usePerActionProposalKpiDeltas } from '../hooks/use-per-action-proposal-kpi-deltas.hook';
import { useProposal } from '../hooks/use-proposals.hooks';
import { ActionListItem } from './ActionListItem';
import { ProposalKpiSection } from './ProposalKpiSection';
import { ElementListTree } from '@features/proposals/ElementListRow';

const useStyles = makeStyles()({
  panel: {
    flex: '1 1 auto',
  },
  empty: {
    '&.MuiTypography-root': {
      margin: 0,
    },
    flex: '1 0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px 32px',
    fontSize: 12,
    lineHeight: '16px',
    fontWeight: 400,
    letterSpacing: '0.2px',
    color: 'rgba(0,0,0,0.45)',
    textAlign: 'center',
  },
});

type ActionsListProps = {
  proposalId: ProposalId;
};

/**
 * Renders the list of {@link Action}s for a selected proposal in the SidePanel.
 * Shown when a proposal is active and no element is selected.
 */
export const ActionsList: React.FC<ActionsListProps> = ({ proposalId }) => {
  const { classes } = useStyles();
  const version = useSelectedVersion();
  const { marketEstimates: projectMarketEstimates } = useProject();
  const marketEstimates = useMemo(
    () => projectMarketEstimates ?? createMarketEstimatesSnapshot(),
    [projectMarketEstimates],
  );
  const proposal = useProposal(proposalId);
  const actions = useActionsForProposal(proposalId);
  const perActionKpi = usePerActionProposalKpiDeltas(proposalId);
  const displayCurrency = useDisplayCurrency();
  const loadingSlots = displayCurrency.isEnabled
    ? ('co2e+cost' as const)
    : ('co2e' as const);
  const kpiLoadingSlots = perActionKpi.isComputingMarginals
    ? loadingSlots
    : undefined;
  const navigateToListSelection = useNavigateToElementListSelection();
  const applyChanges = useApplyProjectChanges();

  const condenseConflicts = useMemo(() => {
    if (!version || !proposal) return [];
    return resolveVersionForProposal(version, proposal).conflicts;
  }, [version, proposal]);

  useEffect(() => {
    if (!version || !proposal || condenseConflicts.length === 0) return;
    warnProposalCondenseConflicts(version, proposal, condenseConflicts);
  }, [version, proposal, condenseConflicts]);

  const handleRenameAction = useCallback(
    (actionId: string, name: string) => {
      if (!version) return;
      void applyChanges([
        {
          type: ProjectChangeType.ActionRename,
          versionId: version.id,
          actionId,
          name,
        },
      ]);
    },
    [version, applyChanges],
  );

  /**
   * Removes an action from the selected version by id. The engine handler also unlinks
   * the action from any `proposals[*].actionIds` so the invariant holds.
   */
  const handleRemoveAction = useCallback(
    (actionId: string) => {
      if (!version) return;
      void applyChanges([
        {
          type: ProjectChangeType.ActionDelete,
          versionId: version.id,
          actionId,
        },
      ]);
    },
    [version, applyChanges],
  );

  const handleElementClick = useCallback(
    (elementId: string) => {
      navigateToListSelection(elementId, { page: Page.Elements });
    },
    [navigateToListSelection],
  );

  if (!version || !proposal) return null;

  if (actions.length === 0) {
    return (
      <SidePanel.Content className={classes.panel} gap={0}>
        <Typography className={classes.empty} component="p">
          Changes applied to this proposal will appear here.
        </Typography>
      </SidePanel.Content>
    );
  }

  return (
    <SidePanel.Content className={classes.panel} gap={0}>
      <SidePanel.Content.Section sx={{ px: 0 }}>
        <ProposalKpiSection proposalId={proposalId} />
      </SidePanel.Content.Section>

      <SidePanel.Content.Divider />

      <SidePanel.Content.Section sx={{ px: 0 }}>
        <ElementListTree aria-label="Proposal actions">
          {actions.map((action, index) => (
            <React.Fragment key={action.id}>
              <ActionListItem
                action={action}
                version={version}
                onRename={handleRenameAction}
                onRemove={handleRemoveAction}
                onElementClick={handleElementClick}
                actionCo2eDelta={perActionKpi.co2eByActionId?.[action.id]}
                actionCostDeltaSek={perActionKpi.sekByActionId?.[action.id]}
                marketEstimates={marketEstimates}
                kpiLoadingSlots={kpiLoadingSlots}
              />
              {index < actions.length - 1 && <SidePanel.Content.Divider />}
            </React.Fragment>
          ))}
        </ElementListTree>
      </SidePanel.Content.Section>
    </SidePanel.Content>
  );
};
